import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import { USER_ROLES, type Profile, type UserRole } from '@/types/app';
import { CreateUserDialog } from './CreateUserDialog';
import { useTeams } from './useTeams';
import { useSetUserActive, useSetUserRole, useSetUserTeam, useUsers } from './useUsers';

export default function UsersPage() {
  const { t } = useTranslation();
  const { data: me } = useProfile();
  const { data: users, isPending, isError } = useUsers();
  const { data: teams } = useTeams();

  const setRole = useSetUserRole();
  const setTeam = useSetUserTeam();
  const setActive = useSetUserActive();

  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const roleOptions = USER_ROLES.map((r) => ({
    value: r,
    label: t(`roles.${r}` as 'roles.sales'),
  }));
  const teamOptions = (teams ?? []).map((team) => ({ value: team.id, label: team.name }));

  // The database refuses these anyway (admin_set_role rejects self-changes and the
  // removal of the last admin). Disabling the control just avoids offering an action
  // that is going to fail.
  const isSelf = (user: Profile) => user.id === me?.id;

  const run = (action: Promise<unknown>) => {
    setFailure(null);
    action.catch((cause: unknown) => {
      setFailure(cause instanceof Error ? cause.message : t('common.error'));
    });
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">{t('nav.users')}</h1>
        <Button onClick={() => setCreating(true)}>{t('admin.users.create')}</Button>
      </div>

      {failure ? <Alert tone="error">{failure}</Alert> : null}
      {isError ? <Alert tone="error">{t('common.error')}</Alert> : null}

      {isPending ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-start text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.users.fullName')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('auth.email')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.users.role')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.users.team')}
                </th>
                <th scope="col" className="px-3 py-2 text-start">
                  {t('admin.users.status')}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {(users ?? []).map((user) => (
                <tr key={user.id}>
                  <td className="px-3 py-2 font-medium text-slate-900">{user.full_name}</td>
                  <td className="px-3 py-2 text-slate-600" dir="ltr">
                    {user.email}
                  </td>

                  <td className="px-3 py-2">
                    <Select
                      hideLabel
                      label={t('admin.users.role')}
                      value={user.role}
                      disabled={isSelf(user) || setRole.isPending}
                      options={roleOptions}
                      onChange={(e) =>
                        run(
                          setRole.mutateAsync({
                            userId: user.id,
                            role: e.target.value as UserRole,
                            reason: t('admin.users.changedByAdmin'),
                          }),
                        )
                      }
                    />
                  </td>

                  <td className="px-3 py-2">
                    <Select
                      hideLabel
                      label={t('admin.users.team')}
                      placeholder={t('admin.users.noTeam')}
                      value={user.team_id ?? ''}
                      disabled={setTeam.isPending}
                      options={teamOptions}
                      onChange={(e) =>
                        run(
                          setTeam.mutateAsync({
                            userId: user.id,
                            teamId: e.target.value || null,
                            reason: t('admin.users.changedByAdmin'),
                          }),
                        )
                      }
                    />
                  </td>

                  <td className="px-3 py-2">
                    <Button
                      variant={user.is_active ? 'secondary' : 'primary'}
                      disabled={isSelf(user) || setActive.isPending}
                      onClick={() =>
                        run(
                          setActive.mutateAsync({
                            userId: user.id,
                            active: !user.is_active,
                            reason: t('admin.users.changedByAdmin'),
                          }),
                        )
                      }
                    >
                      {user.is_active ? t('admin.users.deactivate') : t('admin.users.activate')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserDialog open={creating} teams={teams ?? []} onClose={() => setCreating(false)} />
    </section>
  );
}
